/**
 * Button semantics + keyboard activation for a selectable avatar-face tile.
 *
 * Both face pickers render their tiles as styled `Card`s (divs), which are
 * click-only: no `role`, no tab stop, no Enter/Space. That makes the whole
 * picker unusable without a mouse, and it conveys nothing about which tile is
 * chosen to a screen reader. Shared here rather than inlined because the two
 * pickers (`AvatarFacePicker`, `AvatarPickerButton`) render the SAME tile twice
 * each — the "None" card and the face cards — so the fix would otherwise be
 * copy-pasted four times and drift.
 *
 * `aria-pressed` (not `role="radio"`) matches how these tiles actually behave:
 * each is an independently activatable toggle whose selected state is visible,
 * and the group has no roving-tabindex arrow navigation to back radio
 * semantics up. Enter and Space both activate — Space is `preventDefault`ed so
 * it selects instead of scrolling the page, the same pattern a native
 * `<button>` implements and the one already used elsewhere in this codebase
 * (e.g. `EntityClaimsSection`).
 */
import type { KeyboardEvent } from 'react';

interface SelectableTileOptions {
  /** True when this tile is the current selection. */
  selected: boolean;
  /** True when the whole picker is disabled. */
  disabled: boolean;
  /** Invoked on click, Enter, or Space. */
  onSelect: () => void;
}

interface SelectableTileProps {
  role: 'button';
  tabIndex: number;
  'aria-pressed': boolean;
  'aria-disabled': boolean | undefined;
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

export function selectableTileProps({
  selected,
  disabled,
  onSelect,
}: SelectableTileOptions): SelectableTileProps {
  const activate = () => {
    if (!disabled) onSelect();
  };
  return {
    role: 'button',
    // A disabled tile stays readable but leaves the tab order entirely.
    tabIndex: disabled ? -1 : 0,
    'aria-pressed': selected,
    'aria-disabled': disabled || undefined,
    onClick: activate,
    onKeyDown: (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault(); // Space would otherwise scroll the page
        activate();
      }
    },
  };
}

/** Focus ring for a tile, matching the shared Button's focus-visible treatment. */
export const SELECTABLE_TILE_FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
